import * as React from 'react';

import { FormFieldType, ValidationBarTheme } from '../../enums';
import { AddPhotoFile, DropdownOption, SuggestionItem } from '../../interfaces';
import { getSingleValueFromFormFieldValue } from '../../utils';
import { AddPhoto } from '../addPhoto/addPhoto.component';
import { Autosuggest } from '../autosuggest/autosuggest.component';
import { Dropdown } from '../dropdown/dropdown.component';
import { FullscreenDropdown } from '../fullscreenDropdown/fullscreenDropdown.component';
import { Input } from '../input/input.component';
import { Textarea } from '../textarea/textarea.component';

import { Container, FieldContainer, StyledValidationBar } from './formField.styled';
import { FormFieldProps } from './formField.types';

export const FormField: React.FC<FormFieldProps> = (props: FormFieldProps): JSX.Element => {
  const {
    fieldType,
    setFieldValue,
    disabled,
    value = '',
    label = '',
    fieldName,
    additionalFieldProps,
    description,
    validationMessage,
    withfullWidthFieldContainer = false,
    actions,
    className
  }: FormFieldProps = props;

  const addFileAction: (file: File, onProgress: (progressEvent: ProgressEvent<FileReader>) => void) => Promise<AddPhotoFile> = (
    file: File, onProgress: (progressEvent: ProgressEvent<FileReader>) => void
  ): Promise<AddPhotoFile> => {
    if (actions?.addFile && additionalFieldProps?.attachmentType) {
      return actions.addFile(file, onProgress, additionalFieldProps.attachmentType, fieldName);
    }

    return new Promise<AddPhotoFile>((): void => undefined);
  };

  return (
    <Container className={className}>
      {description?.position === 'top' && (
        <StyledValidationBar
          message={description.content}
          barTheme={ValidationBarTheme.Information}
          $position='top'
        />
      )}
      <FieldContainer $fullWidth={withfullWidthFieldContainer}>
        {fieldType === FormFieldType.Input && (
          <Input
            label={label}
            value={getSingleValueFromFormFieldValue<string>(value)}
            disabled={disabled}
            onChange={(valueFromEvent: string): void => setFieldValue(fieldName, valueFromEvent)}
            onBlur={(valueFromEvent: string): void => setFieldValue(fieldName, valueFromEvent)}
            type={additionalFieldProps?.type}
            mask={additionalFieldProps?.mask}
            format={additionalFieldProps?.format}
            withOnlyNumbersAllowed={additionalFieldProps?.withOnlyNumbersAllowed}
            withPriceParsing={additionalFieldProps?.withPriceParsing}
          />
        )}
        {fieldType === FormFieldType.Textarea && (
          <Textarea
            label={label}
            maxCharsCount={additionalFieldProps?.maxCharsCount}
            value={getSingleValueFromFormFieldValue<string>(value)}
            disabled={disabled}
            onChange={(valueFromEvent: string): void => setFieldValue(fieldName, valueFromEvent)}
            onBlur={(valueFromEvent: string): void => setFieldValue(fieldName, valueFromEvent)}
          />
        )}
        {fieldType === FormFieldType.Dropdown && (
          <Dropdown
            label={label}
            initialValueName={getSingleValueFromFormFieldValue<string>(value)}
            onChooseAnswer={(option: DropdownOption): void => setFieldValue(fieldName, option.name)}
            options={additionalFieldProps?.options ?? []}
            disabled={disabled}
          />
        )}
        {fieldType === FormFieldType.FullscreenDropdown && (
          <FullscreenDropdown
            label={label}
            activeValuesNames={Array.isArray(value) ? value as string[] : undefined}
            onChooseAnswers={
              (options: DropdownOption[]): void => setFieldValue(fieldName, options.map((option: DropdownOption): string => option.name))
            }
            options={additionalFieldProps?.options ?? []}
            disabled={disabled}
            multiple={!!additionalFieldProps?.multiple}
          />
        )}
        {fieldType === FormFieldType.Autosuggest && (
          <Autosuggest
            label={label}
            initialSuggestion={getSingleValueFromFormFieldValue<SuggestionItem>(value)}
            initialSuggestionLabel={additionalFieldProps?.initialValueLabel}
            suggestions={additionalFieldProps?.suggestions ?? []}
            onSelect={(suggestion: SuggestionItem): void => setFieldValue(fieldName, suggestion)}
            onClear={(): void => setFieldValue(fieldName, '')}
            onValueInputChange={actions?.autosuggestValueInput}
            onValueInputChangeAsync={actions?.autosuggestValueInputAsync}
            withAcceptNotSuggestedValue={additionalFieldProps?.withAcceptNotSuggestedValue}
            onSelectNotSuggestedValuePromise={additionalFieldProps?.onSelectNotSuggestedValuePromise}
            disabled={disabled}
          />
        )}
      </FieldContainer>
      {fieldType === FormFieldType.AddPhoto && (
        <AddPhoto
          addPhotoTheme={additionalFieldProps?.addPhotoTheme}
          addFile={addFileAction}
          removeFile={actions?.removeFile}
          onFilesListChange={(files: AddPhotoFile[]): void => setFieldValue(fieldName, files)}
          files={additionalFieldProps?.files}
          requirements={additionalFieldProps?.requirements}
          acceptedFilesTypes={additionalFieldProps?.acceptedFilesTypes}
          maxFileSizeInBytes={additionalFieldProps?.maxFileSizeInBytes}
          maxNumberOfFiles={additionalFieldProps?.maxNumberOfFiles}
        />
      )}
      {description?.position === 'bottom' && (
        <StyledValidationBar
          message={description.content}
          barTheme={ValidationBarTheme.Information}
          $position='bottom'
        />
      )}
      <StyledValidationBar message={validationMessage} $position='bottom' />
    </Container>
  );
};
